import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { FaMapMarked } from 'react-icons/fa';
import { IoImagesOutline } from 'react-icons/io5';
import { jwtDecode } from 'jwt-decode';
import { FaWhatsapp } from 'react-icons/fa';
import { IoMdCall } from 'react-icons/io';
import { MdEmail, MdOutlineConnectWithoutContact } from 'react-icons/md';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import '../../style/LostPetDetailsPage.css';
import petSpecies from '../../data/petSpecies';
import { useTranslation } from 'react-i18next'; // Import useTranslation hook
import i18n from '../../i18n'; // Import i18n instance

const MAP_CONTAINER_STYLE = { width: '100%', height: '300px' };

const LostPetDetailsPage = ({ isLoaded }) => {
    const { id } = useParams();
    const [pet, setPet] = useState(null);
    const [view, setView] = useState('map'); // 'map' or 'images'
    const [error, setError] = useState(null);
    const [reportedByContact, setReportedByContact] = useState(null);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const [suggestionsError, setSuggestionsError] = useState(null);
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const navigate = useNavigate();
    const { t } = useTranslation(); // Initialize useTranslation

    const { isLoaded: isMapLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries: ['places', 'maps'], // Ensure consistent libraries across the app
    });

    // Get the logged-in user
    const storedUser = JSON.parse(localStorage.getItem('user'));
    console.log('storedUser:', storedUser);
    let userId;
    if (storedUser?.token) {
        const decoded = jwtDecode(storedUser.token);
        userId = decoded.id || decoded._id || decoded.googleId || decoded.email;
    }
    console.log('userId:', userId);

    useEffect(() => {
        let isMounted = true; // To prevent state updates on unmounted component

        const fetchLostPet = async () => {
            try {
                const response = await axios.get(`${baseUrl}/api/pets/lost/${id}`);
                if (isMounted) {
                    setPet(response.data);
                }
            } catch (error) {
                if (isMounted) {
                    setError(error.response?.data?.message || error.message);
                }
            }
        };

        fetchLostPet();

        return () => {
            isMounted = false; // Cleanup function to prevent memory leaks
        };
    }, [id, baseUrl]);

    useEffect(() => {
        const fetchReportedByContact = async () => {
            console.log('pet.lastLostInfo?.reportedBy:', pet?.lastLostInfo?.reportedBy); // Log reportedBy field

            if (pet?.lastLostInfo?.reportedBy?._id) {
                try {
                    const response = await axios.get(
                        `${baseUrl}/api/users/public-info/${pet.lastLostInfo.reportedBy._id}`
                    );
                    console.log('API response for reportedByContact:', response.data); // Log API response
                    setReportedByContact(response.data);
                } catch (error) {
                    console.error('Error fetching reportedBy contact info:', error);
                    setReportedByContact({}); // Fallback to an empty object
                }
            } else {
                console.log('No reportedBy ID found, setting reportedByContact to empty object.');
                setReportedByContact({}); // Fallback to an empty object if reportedBy is not available
            }
        };

        fetchReportedByContact();
    }, [pet?.lastLostInfo?.reportedBy?._id, baseUrl]);

    // Fetch found pet suggestions (like FoundPetDetailsPage.js)
    useEffect(() => {
        if (!pet || !pet.lastLostInfo?.lastSeenLocation?.coordinates || !pet.petType) return;
        const [lng, lat] = pet.lastLostInfo.lastSeenLocation.coordinates;
        setSuggestionsLoading(true);
        setSuggestionsError(null);
        axios.get(`${baseUrl}/api/found-pets/found-suggestions?lat=${lat}&lng=${lng}&petType=${pet.petType}`)
            .then(response => setSuggestions(response.data))
            .catch(err => setSuggestionsError(err.message || 'Error fetching suggestions'))
            .finally(() => setSuggestionsLoading(false));
    }, [pet, baseUrl]);


    if (loadError) {
        return <div style={{ color: 'red', textAlign: 'center' }}>{t('reportPetLostPage.error.mapLoadFailed')} {loadError.message}</div>;
    }

    if (error) {
        return <div style={{ color: 'red', textAlign: 'center' }}>Error: {error}</div>;
    }

    if (!pet) {
        return <div style={{ textAlign: 'center' }}>{t('general.loading')}</div>;
    }

    const calculateAge = (birthdate) => {
        if (!birthdate) return t('general.unknown');
        const birthDate = new Date(birthdate);
        const today = new Date();

        const years = today.getFullYear() - birthDate.getFullYear();
        const months = today.getMonth() - birthDate.getMonth();
        const days = today.getDate() - birthDate.getDate();

        let ageString = '';

        if (years > 0) {
            ageString += `${years} ${t('general.year', { count: years })} `;
        }
        if (months > 0) {
            ageString += `${months} ${t('general.month', { count: months })} `;
        }
        if (years === 0 && months === 0 && days > 0) {
            ageString += `${days} ${t('general.day', { count: days })}`;
        }

        return ageString.trim() || t('general.unknownAge');
    };

    const profileImage = pet.images?.find(image => image.tag === 'profile')?.url || pet.images?.find(image => image.tag !== 'lost')?.url;

    console.log('organizationId:', pet.organizationId);
    console.log('userId:', userId);

    // Check if the user is an Admin or Owner within the organization
    const isEligible = pet.organizationId?.members?.some(member => member.userId === userId && ['admin', 'owner'].includes(member.role));

    const contactInfo = pet.lastLostInfo?.reportedBy;

    const lostImages = pet.images?.filter(image => image.tag === 'lost') || [];
    const imageUrls = lostImages.map(image => image.url);

    const getPetTypeLabel = (petTypeValue) => {
        const species = petSpecies.find(species => species.value === petTypeValue);
        return species ? species.label[i18n.language] || species.label.en : petTypeValue;
    };

    return (
        <div className="lost-pet-details-page">
            {/* Header Section */}
            <div className="lost-pet-header">
                <div className="lost-pet-header-left">
                    {profileImage ? (
                        <img
                            src={profileImage} // Use the profile image or fallback to the first image
                            alt={`${pet.name} profile`}
                            className="pet-profile-picture"
                        />
                    ) : (
                        <div className="placeholder-picture text-dafauna-gray">{t('generalPet.imageAvailable')}</div> // Placeholder if no image exists
                    )}
                    <div className="pet-details">
                        <h1 className="pet-name">{pet.name}</h1>
                        <p className="pet-type align-items-center">{t('lostPetDetailsPage.petTypeAndAge', {
                            petType: getPetTypeLabel(pet.petType),
                            age: calculateAge(pet.birthDate),
                        })}</p>
                        {/* Report Found Button - Visible only to admins or organization owners */}
                        {isEligible && (
                            <button
                                className="report-found-btn"
                                onClick={() => navigate(`/report-lost-pet-found/${pet._id}`)}
                            >
                                {t('lostPetDetailsPage.reportFound')}
                            </button>
                        )}
                    </div>
                </div>
                <div className="lost-pet-header-right">
                    <p className="contact">
                        {t('lostPetDetailsPage.contactInfo')}
                    </p>
                    <div className="contact-icons">
                    {reportedByContact?.phoneNumber && (
                        <a
                        href={`https://wa.me/${reportedByContact.phoneNumber.replace(/^\+/, '')}?text=${t('lostPetDetailsPage.whatsappMessage')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contact-icon whatsapp"
                        >
                            <FaWhatsapp />
                        </a>
                    )}
                    {reportedByContact?.phoneNumber && (
                        <a
                        href={`tel:${reportedByContact.phoneNumber}`}
                        className="contact-icon call"
                        >
                            <IoMdCall />
                        </a>
                    )}
                    {reportedByContact?.email && (
                        <a
                        href={`mailto:${reportedByContact.email}?subject=${t('lostPetDetailsPage.lostPetInquirySubject')}&body=${t('lostPetDetailsPage.lostPetInquiryBody')}`}
                        className="contact-icon email"
                        >
                            <MdEmail />
                        </a>
                    )}
                    </div>
                </div>
            </div>

            {/* Lost Details Section */}
            <div className="lost-details">
                <h3>{t('lostPetDetailsPage.lostDetails')}</h3>
                {pet.lastLostInfo ? (
                    <div>
                        <p><strong>{t('lostPetDetailsPage.lostOn')}</strong> {new Date(pet.lastLostInfo.lastSeenDate).toLocaleString()}</p>
                        <p><strong>{t('lostPetDetailsPage.description')}</strong> {pet.lastLostInfo.description || t('lostPetDetailsPage.noDescription')}</p>
                    </div>
                ) : (
                    <p>{t('lostPetDetailsPage.noLostDetails')}</p>
                )}
            </div>

            {/* View Toggle Section */}
            <div className="section-navigation">
                <button
                    className={`nav-btn ${view === 'map' ? 'active' : ''}`}
                    onClick={() => setView('map')}
                >
                    <FaMapMarked />
                </button>
                <button
                    className={`nav-btn ${view === 'images' ? 'active' : ''}`}
                    onClick={() => setView('images')}
                >
                    <IoImagesOutline />
                </button>
                                <button
                    className={`nav-btn ${view === 'suggestions' ? 'active' : ''}`}
                    onClick={() => setView('suggestions')}
                    title={t('lostPetDetailsPage.foundPetSuggestions') || 'Found Pet Suggestions'}
                >
                    <MdOutlineConnectWithoutContact />
                </button>
            </div>            

            {/* Map View */}
            {view === 'map' && isLoaded && pet.lastLostInfo?.lastSeenLocation?.coordinates ? (
                <GoogleMap
                    mapContainerStyle={MAP_CONTAINER_STYLE}
                    center={{
                        lat: pet.lastLostInfo.lastSeenLocation.coordinates[1],
                        lng: pet.lastLostInfo.lastSeenLocation.coordinates[0],
                    }}
                    zoom={15}
                >
                    <Marker
                        position={{
                            lat: pet.lastLostInfo.lastSeenLocation.coordinates[1],
                            lng: pet.lastLostInfo.lastSeenLocation.coordinates[0],
                        }}
                    />
                </GoogleMap>
            ) : null}

                        {/* Suggestions Section */}
            {view === 'suggestions' && (
                <div className="found-pet-suggestions-section" style={{ margin: '24px 0' }}>
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MdEmail size={24} style={{ color: '#2980B9' }} />
                        {t('lostPetDetailsPage.foundPetSuggestions') || 'Found Pet Suggestions Nearby'}
                    </h3>
                    {suggestionsLoading && <div>{t('general.loading') || 'Loading suggestions...'}</div>}
                    {suggestionsError && <div style={{ color: 'red' }}>{suggestionsError}</div>}
                    {!suggestionsLoading && !suggestionsError && suggestions.length === 0 && (
                        <div style={{ color: '#888' }}>{t('lostPetDetailsPage.noSuggestions') || 'No found pets nearby.'}</div>)}
                    {!suggestionsLoading && !suggestionsError && suggestions.length > 0 && (
                        <ul className="found-pet-suggestions-list" style={{ listStyle: 'none', padding: 0 }}>
                            {suggestions.map((foundPet) => {
                                const foundImage = Array.isArray(foundPet.images) && foundPet.images.length > 0 ? foundPet.images[0].url || foundPet.images[0] : null;
                                return (
                                    <li key={foundPet._id} className="found-pet-suggestion-item" style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, background: '#EAF2F8', borderRadius: 8, padding: 12 }}>
                                        {foundImage ? (
                                            <img src={foundImage} alt={foundPet.name} style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: '50%' }} />
                                        ) : (
                                            <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#FADBD7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8A8A8A', fontSize: 24 }}>?</div>
                                        )}
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 600 }}>{foundPet.name}</div>
                                            <div style={{ color: '#888' }}>{getPetTypeLabel(foundPet.petType)}</div>
                                            <div style={{ fontSize: 13, color: '#666' }}>{foundPet.foundDateTime ? `${t('lostPetDetailsPage.foundOn') || 'Found on'}: ${new Date(foundPet.foundDateTime).toLocaleString()}` : ''}</div>
                                        </div>
                                        <button className="bg-dafauna-primary text-white rounded-md px-3 py-1 hover:bg-dafauna-primaryLight" onClick={() => navigate(`/found-pet/${foundPet._id}`)}>
                                            {t('lostPetDetailsPage.viewDetails') || 'View Details'}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}

            {/* Images Section */}
            {view === 'images' && Array.isArray(lostImages) && lostImages.length > 0 ? (
                <div className="images-section">
                    {lostImages.map((image, index) => (
                        <img
                            key={index}
                            src={image.url}
                            alt={`Image ${index + 1}`}
                            onClick={() => {
                                setCurrentImageIndex(index);
                                setIsLightboxOpen(true);
                            }}
                        />
                    ))}
                </div>
            ) : null}

            {isLightboxOpen && (
                <Lightbox
                    mainSrc={imageUrls[currentImageIndex]}
                    nextSrc={imageUrls[(currentImageIndex + 1) % imageUrls.length]}
                    prevSrc={imageUrls[(currentImageIndex + imageUrls.length - 1) % imageUrls.length]}
                    onCloseRequest={() => setIsLightboxOpen(false)}
                    onMovePrevRequest={() =>
                        setCurrentImageIndex((currentImageIndex + imageUrls.length - 1) % imageUrls.length)
                    }
                    onMoveNextRequest={() =>
                        setCurrentImageIndex((currentImageIndex + 1) % imageUrls.length)
                    }
                />
            )}

            
        </div>
    );
};

export default LostPetDetailsPage;